import {
  draggable,
  dropTargetForElements,
} from '@atlaskit/pragmatic-drag-and-drop/adapter/element-adapter';
import { combine } from '@atlaskit/pragmatic-drag-and-drop/utils/combine';
import { attachClosestEdge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge/attach-closest-edge';
import { extractClosestEdge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge/extract-closest-edge';
import type { Edge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/types';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { type Label, LabelChip } from '@/entities/board';
import { Button } from '@/shared/ui';

import { boardLabelData, isBoardLabel } from './dnd';
import styles from './LabelManager.module.css';

interface Props {
  boardId: string;
  label: Label;
  reorderable: boolean;
  onEdit: () => void;
}

/** One label of the board manager. The whole row is the drag handle (no arrow buttons). */
export function LabelRow({ boardId, label, reorderable, onEdit }: Props) {
  const { t } = useTranslation();
  const ref = useRef<HTMLLIElement>(null);
  const [dragging, setDragging] = useState(false);
  const [edge, setEdge] = useState<Edge | null>(null);
  const name = label.name || t('labels.unnamed');

  useEffect(() => {
    const element = ref.current;
    if (!element || !reorderable) return;
    return combine(
      draggable({
        element,
        getInitialData: () => boardLabelData(boardId, label.id),
        onDragStart: () => {
          setDragging(true);
        },
        onDrop: () => {
          setDragging(false);
        },
      }),
      dropTargetForElements({
        element,
        canDrop: ({ source }) =>
          isBoardLabel(source.data) &&
          source.data.boardId === boardId &&
          source.data.labelId !== label.id,
        getData: ({ input }) =>
          attachClosestEdge(boardLabelData(boardId, label.id), {
            element,
            input,
            allowedEdges: ['top', 'bottom'],
          }),
        onDrag: ({ self }) => {
          setEdge(extractClosestEdge(self.data));
        },
        onDragLeave: () => {
          setEdge(null);
        },
        onDrop: () => {
          setEdge(null);
        },
      }),
    );
  }, [boardId, label.id, reorderable]);

  return (
    <li
      ref={ref}
      className={[
        styles.row,
        reorderable && styles.draggable,
        dragging && styles.dragging,
        edge && styles[`drop-${edge}`],
      ]
        .filter(Boolean)
        .join(' ')}
      data-testid="board-label"
      title={reorderable ? t('labels.dragHint') : undefined}
    >
      {reorderable && (
        <span className={styles.grip} aria-hidden>
          ⠿
        </span>
      )}
      <span className={styles.chipCell}>
        <LabelChip label={label} emptyName={t('labels.unnamed')} />
      </span>
      <span className={styles.rowActions}>
        <Button size="sm" aria-label={t('labels.editOne', { name })} onClick={onEdit}>
          {t('labels.editShort')}
        </Button>
      </span>
    </li>
  );
}
